import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, Send } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Custom House Agency",
  "Freight Forwarding",
  "Importer–Exporter Consultancy",
  "Trademark Filing & IP",
  "Incorporation & Compliances",
  "GST & IEC Registration",
  "Other",
];

const inputCls =
  "w-full bg-[#0A0E17] border border-white/15 rounded-sm px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-[#F59E0B] focus:outline-none transition-colors duration-300";

export default function Contact({ preselectedService }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (preselectedService) setForm((f) => ({ ...f, service: preselectedService }));
  }, [preselectedService]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. Our desk will respond within 24 hours.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please call or WhatsApp us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 bg-[#111726] border-t border-white/[0.06] noise-overlay">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="lg:col-span-2"
        >
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-amber-400 mb-4">
            Open a File With Us
          </p>
          <h2 className="font-display font-bold tracking-tight uppercase text-2xl sm:text-3xl lg:text-5xl text-slate-100 leading-tight">
            Tell us what <span className="outline-text">moves.</span>
          </h2>
          <p className="mt-6 text-base text-slate-300 leading-relaxed max-w-sm">
            Share a few details and the SHIVIN INFOTECH desk responds within one
            working day. Prefer talking? We answer directly.
          </p>

          <div className="mt-10 space-y-4">
            <a
              data-testid="contact-phone-1"
              href="tel:7407037269"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 grid place-items-center border border-white/10 rounded-sm text-[#F59E0B] group-hover:bg-[#F59E0B] group-hover:text-[#0A0E17] transition-colors duration-300">
                <Phone size={17} />
              </span>
              <span className="font-mono2 text-sm text-slate-200 group-hover:text-white transition-colors duration-300">+91 74070 37269</span>
            </a>
            <a
              data-testid="contact-phone-2"
              href="tel:7908611427"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 grid place-items-center border border-white/10 rounded-sm text-[#F59E0B] group-hover:bg-[#F59E0B] group-hover:text-[#0A0E17] transition-colors duration-300">
                <Phone size={17} />
              </span>
              <span className="font-mono2 text-sm text-slate-200 group-hover:text-white transition-colors duration-300">+91 79086 11427</span>
            </a>
            <a
              data-testid="contact-email-link"
              href="mailto:director@transgistics.in"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 grid place-items-center border border-white/10 rounded-sm text-[#F59E0B] group-hover:bg-[#F59E0B] group-hover:text-[#0A0E17] transition-colors duration-300">
                <Mail size={17} />
              </span>
              <span className="font-mono2 text-sm text-slate-200 group-hover:text-white transition-colors duration-300">director@transgistics.in</span>
            </a>
            <a
              data-testid="contact-whatsapp-link"
              href="https://wa.me/917407037269"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 mt-2 bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] font-mono2 text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-full hover:bg-[#10B981] hover:text-[#0A0E17] transition-colors duration-300"
            >
              <MessageCircle size={16} /> WhatsApp the desk
            </a>
          </div>
        </motion.div>

        <motion.form
          data-testid="enquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="lg:col-span-3 bg-[#161F33] border border-white/[0.08] rounded-md p-6 sm:p-10"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Your name *</label>
              <input data-testid="enquiry-input-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
            </div>
            <div>
              <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Mobile *</label>
              <input data-testid="enquiry-input-mobile" required minLength={8} value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className={inputCls} />
            </div>
            <div>
              <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Email *</label>
              <input data-testid="enquiry-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.in" className={inputCls} />
            </div>
            <div>
              <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Service needed *</label>
              <select data-testid="enquiry-select-service" required value={form.service} onChange={set("service")} className={inputCls}>
                <option value="" disabled>Select a service</option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Message *</label>
              <textarea data-testid="enquiry-input-message" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Cargo type, port, timeline — or the registration you need…" className={`${inputCls} resize-none`} />
            </div>
          </div>
          <button
            data-testid="enquiry-submit-button"
            type="submit"
            disabled={sending}
            className="mt-8 w-full sm:w-auto flex items-center justify-center gap-3 bg-[#F59E0B] text-[#0A0E17] font-display font-bold uppercase tracking-wide text-sm px-10 py-4 rounded-full hover:bg-white transition-colors duration-300 disabled:opacity-60"
          >
            {sending ? "Filing…" : "File enquiry"} <Send size={16} />
          </button>
          <p className="mt-4 font-mono2 text-[10px] uppercase tracking-wider text-slate-500">
            Every enquiry lands directly in the director's inbox.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
